// src/pages/CancellationRefundPolicy.jsx
import React, { useEffect, useState } from "react";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const CANCELLATION_COPY = {
  es: {
    title: "Política de Cancelación y Reembolsos",
    updated: "Última actualización: junio de 2026",
    intro: (
      <>
        Esta Política explica las condiciones de cancelación, acceso posterior a
        la cancelación, prueba gratuita, renovaciones y reembolsos aplicables a{" "}
        <strong>ZEDRO Academy Membership</strong>, la membresía mensual del
        ecosistema formativo de ZEDRO Academy.
      </>
    ),
    sections: [
      {
        title: "1. Producto al que aplica esta Política",
        content: (
          <>
            Esta Política aplica a ZEDRO Academy Membership, una membresía
            mensual que da acceso al ecosistema formativo digital de ZEDRO
            Academy, incluyendo sistemas formativos, recursos prácticos,
            Bitácora ZEDRO, Conexión ZEDRO y otras funcionalidades disponibles
            dentro de la plataforma.
          </>
        ),
      },
      {
        title: "2. Prueba gratuita de 7 días",
        content: (
          <>
            La membresía puede iniciar con una prueba gratuita de 7 días con
            tarjeta registrada. No se realiza ningún cobro durante la prueba
            gratuita. Si el usuario cancela antes de finalizar los 7 días de
            prueba, no se realizará el primer cobro mensual.
          </>
        ),
      },
      {
        title: "3. Activación automática después de la prueba",
        content: (
          <>
            Si el usuario no cancela antes de finalizar la prueba gratuita, la
            membresía mensual se activará automáticamente y se cobrará el precio
            vigente mostrado antes de confirmar el pago, junto con los impuestos
            aplicables, si corresponden.
          </>
        ),
      },
      {
        title: "4. Renovación mensual",
        content: (
          <>
            Después del primer cobro, la membresía se renueva mensualmente hasta
            que el usuario la cancele o hasta que ZEDRO Academy suspenda o
            finalice el acceso conforme a los Términos y Condiciones y la ley
            aplicable.
          </>
        ),
      },
      {
        title: "5. Cómo cancelar",
        content: (
          <>
            El usuario puede cancelar su membresía desde su cuenta, cuando esta
            función esté disponible, o escribiendo a{" "}
            <a href="mailto:support@zedro.academy">support@zedro.academy</a>.
            Para evitar un nuevo cobro, la cancelación debe realizarse antes de
            la fecha de renovación o antes de finalizar la prueba gratuita, según
            corresponda.
          </>
        ),
      },
      {
        title: "6. Efecto de la cancelación",
        content: (
          <>
            La cancelación evita futuras renovaciones y futuros cobros. Si el
            usuario cancela durante la prueba gratuita antes de su finalización,
            no se realizará el primer cobro mensual. Si el usuario cancela
            después de un cobro mensual, conservará el acceso hasta el final del
            periodo ya pagado, salvo que la ley aplicable exija o reconozca un
            derecho distinto.
          </>
        ),
      },
      {
        title: "7. Reembolsos y pagos proporcionales",
        content: (
          <>
            Los pagos ya realizados no son reembolsables por cancelación
            anticipada del mes en curso y no se realizan reembolsos
            proporcionales por días no utilizados, salvo que la ley aplicable
            exija lo contrario.
          </>
        ),
      },
      {
        title: "8. Errores técnicos, cobros duplicados o situaciones especiales",
        content: (
          <>
            Si se produce un cobro por error técnico, un cobro duplicado, una
            situación de fraude comprobado o un caso que deba revisarse conforme
            a la ley aplicable, el usuario puede contactar a ZEDRO Academy para
            que la situación sea evaluada. Cuando proceda, ZEDRO Academy
            realizará el ajuste, corrección o reembolso correspondiente.
          </>
        ),
      },
      {
        title: "9. Acceso progresivo y cancelación",
        content: (
          <>
            ZEDRO Academy funciona con acceso progresivo. La cancelación de la
            membresía no otorga derecho automático a recibir contenido futuro,
            recursos no activados o materiales que formen parte de etapas
            posteriores del proceso formativo, salvo que la ley aplicable exija
            lo contrario.
          </>
        ),
      },
      {
        title: "10. Idioma del contenido",
        content: (
          <>
            El contenido formativo principal de ZEDRO Academy está disponible
            inicialmente en español. Las condiciones esenciales de compra,
            cancelación, privacidad, cookies y documentos legales están
            disponibles en español y francés.
          </>
        ),
      },
      {
        title: "11. Procesamiento de pagos",
        content: (
          <>
            Los pagos se procesan mediante Stripe u otros proveedores de pago
            autorizados. ZEDRO Academy no almacena números completos de tarjeta
            de crédito o débito. Stripe u otros proveedores pueden emitir
            recibos, facturas o comprobantes de pago conforme a sus propios
            sistemas y configuraciones.
          </>
        ),
      },
      {
        title: "12. Confirmación de compra y copia conservable",
        content: (
          <>
            ZEDRO Academy podrá enviar al usuario una confirmación de la compra,
            activación de prueba gratuita o suscripción, junto con la información
            esencial de la membresía y enlaces a los documentos legales
            aplicables, para que el usuario pueda conservar la información de su
            contratación.
          </>
        ),
      },
      {
        title: "13. Contacto",
        content: (
          <>
            Para preguntas sobre cancelación, reembolsos, acceso, pagos o
            membresía, puedes escribir a{" "}
            <a href="mailto:support@zedro.academy">support@zedro.academy</a>.
            Para asuntos legales o administrativos, puedes escribir a{" "}
            <a href="mailto:admin@zedro.academy">admin@zedro.academy</a>.
          </>
        ),
      },
      {
        title: "14. Relación con otros documentos legales",
        content: (
          <>
            Esta Política complementa los{" "}
            <a href="/legal/terminos">Términos y Condiciones</a>, la{" "}
            <a href="/legal/privacidad">Política de Privacidad</a>, la{" "}
            <a href="/legal/cookies">Política de Cookies</a>, el{" "}
            <a href="/legal/aviso-legal">Aviso Legal</a> y el{" "}
            <a href="/legal/descargo">Descargo de Responsabilidad</a> de ZEDRO
            Academy.
          </>
        ),
      },
    ],
  },
  fr: {
    title: "Politique d’annulation et de remboursement",
    updated: "Dernière mise à jour : juin 2026",
    intro: (
      <>
        La présente Politique explique les conditions d’annulation, d’accès après
        annulation, d’essai gratuit, de renouvellement et de remboursement
        applicables à <strong>ZEDRO Academy Membership</strong>, l’abonnement
        mensuel à l’écosystème formatif de ZEDRO Academy.
      </>
    ),
    sections: [
      {
        title: "1. Produit visé par la présente Politique",
        content: (
          <>
            La présente Politique s’applique à ZEDRO Academy Membership, un
            abonnement mensuel donnant accès à l’écosystème formatif numérique de
            ZEDRO Academy, notamment aux systèmes formatifs, aux ressources
            pratiques, à Bitácora ZEDRO, à Conexión ZEDRO et aux autres
            fonctionnalités disponibles dans la plateforme.
          </>
        ),
      },
      {
        title: "2. Essai gratuit de 7 jours",
        content: (
          <>
            L’abonnement peut commencer par un essai gratuit de 7 jours avec une
            carte enregistrée. Aucun paiement n’est effectué pendant l’essai
            gratuit. Si l’utilisateur annule avant la fin des 7 jours d’essai,
            le premier paiement mensuel ne sera pas effectué.
          </>
        ),
      },
      {
        title: "3. Activation automatique après l’essai",
        content: (
          <>
            Si l’utilisateur n’annule pas avant la fin de l’essai gratuit,
            l’abonnement mensuel sera activé automatiquement et le prix en
            vigueur affiché avant la confirmation du paiement sera facturé, avec
            les taxes applicables, le cas échéant.
          </>
        ),
      },
      {
        title: "4. Renouvellement mensuel",
        content: (
          <>
            Après le premier paiement, l’abonnement est renouvelé mensuellement
            jusqu’à ce que l’utilisateur l’annule ou jusqu’à ce que ZEDRO Academy
            suspende ou mette fin à l’accès conformément aux Conditions générales
            et à la loi applicable.
          </>
        ),
      },
      {
        title: "5. Comment annuler",
        content: (
          <>
            L’utilisateur peut annuler son abonnement depuis son compte, lorsque
            cette fonction est disponible, ou en écrivant à{" "}
            <a href="mailto:support@zedro.academy">support@zedro.academy</a>.
            Pour éviter un nouveau paiement, l’annulation doit être effectuée
            avant la date de renouvellement ou avant la fin de l’essai gratuit,
            selon le cas.
          </>
        ),
      },
      {
        title: "6. Effet de l’annulation",
        content: (
          <>
            L’annulation évite les renouvellements et paiements futurs. Si
            l’utilisateur annule pendant l’essai gratuit avant sa fin, le premier
            paiement mensuel ne sera pas effectué. Si l’utilisateur annule après
            un paiement mensuel, il conservera l’accès jusqu’à la fin de la
            période déjà payée, sauf si la loi applicable exige ou reconnaît un
            droit différent.
          </>
        ),
      },
      {
        title: "7. Remboursements et montants proportionnels",
        content: (
          <>
            Les paiements déjà effectués ne sont pas remboursables en cas
            d’annulation anticipée du mois en cours et aucun remboursement
            proportionnel n’est effectué pour les jours non utilisés, sauf si la
            loi applicable l’exige autrement.
          </>
        ),
      },
      {
        title: "8. Erreurs techniques, paiements en double ou situations particulières",
        content: (
          <>
            En cas de paiement résultant d’une erreur technique, de paiement en
            double, de fraude confirmée ou de situation devant être examinée
            conformément à la loi applicable, l’utilisateur peut contacter ZEDRO
            Academy afin que la situation soit évaluée. Lorsque cela est
            approprié, ZEDRO Academy effectuera l’ajustement, la correction ou le
            remboursement correspondant.
          </>
        ),
      },
      {
        title: "9. Accès progressif et annulation",
        content: (
          <>
            ZEDRO Academy fonctionne avec un accès progressif. L’annulation de
            l’abonnement ne donne pas automatiquement droit à recevoir du contenu
            futur, des ressources non activées ou des documents faisant partie
            d’étapes ultérieures du processus formatif, sauf si la loi
            applicable l’exige autrement.
          </>
        ),
      },
      {
        title: "10. Langue du contenu",
        content: (
          <>
            Le contenu formatif principal de ZEDRO Academy est actuellement
            disponible en espagnol. Les conditions essentielles d’achat,
            d’annulation, de confidentialité, de cookies et les documents
            juridiques sont disponibles en espagnol et en français.
          </>
        ),
      },
      {
        title: "11. Traitement des paiements",
        content: (
          <>
            Les paiements sont traités par Stripe ou par d’autres fournisseurs de
            paiement autorisés. ZEDRO Academy ne conserve pas les numéros
            complets de carte de crédit ou de débit. Stripe ou d’autres
            fournisseurs peuvent émettre des reçus, factures ou preuves de
            paiement selon leurs propres systèmes et configurations.
          </>
        ),
      },
      {
        title: "12. Confirmation d’achat et copie conservable",
        content: (
          <>
            ZEDRO Academy pourra envoyer à l’utilisateur une confirmation de
            l’achat, de l’activation de l’essai gratuit ou de l’abonnement, avec
            les informations essentielles de l’abonnement et les liens vers les
            documents juridiques applicables, afin que l’utilisateur puisse
            conserver les informations relatives à son contrat.
          </>
        ),
      },
      {
        title: "13. Contact",
        content: (
          <>
            Pour toute question concernant l’annulation, les remboursements,
            l’accès, les paiements ou l’abonnement, vous pouvez écrire à{" "}
            <a href="mailto:support@zedro.academy">support@zedro.academy</a>.
            Pour les questions juridiques ou administratives, vous pouvez écrire
            à <a href="mailto:admin@zedro.academy">admin@zedro.academy</a>.
          </>
        ),
      },
      {
        title: "14. Lien avec les autres documents juridiques",
        content: (
          <>
            La présente Politique complète les{" "}
            <a href="/legal/terminos">Conditions générales</a>, la{" "}
            <a href="/legal/privacidad">Politique de confidentialité</a>, la{" "}
            <a href="/legal/cookies">Politique de cookies</a>, l’{" "}
            <a href="/legal/aviso-legal">Avis légal</a> et l’{" "}
            <a href="/legal/descargo">Avis de non-responsabilité</a> de ZEDRO
            Academy.
          </>
        ),
      },
    ],
  },
};

export default function CancellationRefundPolicy() {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const copy = CANCELLATION_COPY[language] || CANCELLATION_COPY.es;

  return (
    <main
      style={{
        maxWidth: 900,
        margin: "2rem auto",
        padding: "0 1rem",
        fontFamily: "Questrial, sans-serif",
        lineHeight: 1.75,
        color: "#2c3b51",
      }}
    >
      <h1>{copy.title}</h1>

      <p
        style={{
          color: "rgba(44, 59, 81, 0.72)",
          fontSize: "0.95rem",
          marginTop: "-0.4rem",
        }}
      >
        {copy.updated}
      </p>

      <p>{copy.intro}</p>

      {copy.sections.map((section) => (
        <section key={section.title} style={{ marginTop: "1.5rem" }}>
          <h2
            style={{
              color: "#286b94",
              fontSize: "1.2rem",
              marginBottom: "0.45rem",
            }}
          >
            {section.title}
          </h2>

          <div>{section.content}</div>
        </section>
      ))}
    </main>
  );
}